import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, ChevronDown } from 'lucide-react';

const STATUS_COLOR = {
  active:    'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  expired:   'bg-amber-100 text-amber-700',
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('tr-TR');
};

function PackageCard({ cp }) {
  const { t } = useTranslation('customers');
  const [expanded, setExpanded] = useState(false);

  const remaining = cp.remainingSessions || {};
  const total     = cp.totalSessions || {};
  const serviceIds = Object.keys(total);

  const statusKey = { active: 'statusActive', completed: 'statusCompleted', expired: 'statusExpired', canceled: 'statusCanceled' }[cp.status] || 'statusCanceled';
  const colorCls = STATUS_COLOR[cp.status] || 'bg-gray-100 text-gray-500';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div
        className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded((v) => !v)}
      >
        <Package className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{cp.packageName}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${colorCls}`}>
              {t(`customerPackages.${statusKey}`)}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>{t('customerPackages.purchased')}: {formatDate(cp.createdAt)}</span>
            {cp.expiresAt
              ? <span>{t('customerPackages.expires')}: {formatDate(cp.expiresAt)}</span>
              : <span>{t('customerPackages.noExpiry')}</span>
            }
          </div>
          <div className="mt-2 space-y-1">
            {serviceIds.map((svcId) => {
              const rem  = remaining[svcId] ?? 0;
              const tot  = total[svcId] ?? 1;
              const name = (cp.packageServices || []).find(s => s.serviceId === svcId)?.serviceName || svcId;
              const pct  = Math.round((rem / tot) * 100);
              return (
                <div key={svcId}>
                  <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                    <span>{name}</span>
                    <span className="font-medium">{rem}/{tot} {t('customerPackages.sessions')}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${pct > 50 ? 'bg-green-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 mb-2">{t('customerPackages.usageHistory')}</p>
          {(!cp.usageHistory || cp.usageHistory.length === 0) ? (
            <p className="text-xs text-gray-400">{t('customerPackages.historyEmpty')}</p>
          ) : (
            <div className="space-y-1">
              {[...cp.usageHistory].reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="text-gray-400">{h.date}</span>
                  <span className="flex-1">{h.serviceName || h.serviceId}</span>
                  <span className="font-medium">×{h.sessions}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PackagesPanel({ packages }) {
  const { t } = useTranslation('customers');
  const active  = packages.filter(p => p.status === 'active');
  const history = packages.filter(p => p.status !== 'active');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">{t('customerPackages.activeTitle')}</p>
        <p className="text-xs text-gray-400 italic">Paket satmak için adisyon oluşturun</p>
      </div>

      {active.length === 0
        ? <p className="text-sm text-gray-400 text-center py-4">{t('customerPackages.empty')}</p>
        : active.map(cp => <PackageCard key={cp.id} cp={cp} />)
      }

      {history.length > 0 && (
        <>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
            {t('customerPackages.historyTitle')}
          </p>
          {history.map(cp => <PackageCard key={cp.id} cp={cp} />)}
        </>
      )}
    </div>
  );
}
